import type { KnotType } from './knots';

/** A colour slot in a design: an index into the design's own palette. */
export type ColorIndex = number;

/** One entry of a design palette: a real DMC floss colour. */
export interface PaletteEntry {
  /** DMC catalogue code, e.g. "321". "B5200" and "ECRU" are also real codes. */
  dmc: string;
  /** DMC's own name for the colour. */
  name: string;
  /** Screen approximation. Never trust this for buying thread. */
  hex: string;
}

export type BraceletStyle = 'normal' | 'alpha' | 'chain';

/* -------------------------------------------------------------------------- */
/* Normal (diagonal knot) patterns                                            */
/* -------------------------------------------------------------------------- */

/**
 * A normal pattern is: an initial left-to-right ordering of strands, plus a
 * grid of knot types. Colours are NOT stored per cell — they are *derived* by
 * simulating the knots, because the whole point of a diagonal-knot bracelet is
 * that the string order evolves and that evolution is the pattern.
 *
 * Row geometry follows the standard chart convention: with `strandCount = n`
 * (always even), odd-numbered rows tie n/2 knots on pairs
 * (0,1) (2,3) (4,5)..., and even-numbered rows tie n/2 - 1 knots on pairs
 * (1,2) (3,4)..., leaving the two outermost strings idle for that row.
 */
export interface NormalPattern {
  style: 'normal';
  /** Colour index of each strand, indexed by STRAND ID (not position). */
  strandColors: ColorIndex[];
  /**
   * rows[r][k] is the knot type for the k-th knot of row r.
   * Row r has `knotsInRow(strandCount, r)` entries.
   */
  rows: KnotType[][];
}

/* -------------------------------------------------------------------------- */
/* Alpha patterns                                                             */
/* -------------------------------------------------------------------------- */

/**
 * An alpha pattern is a pixel grid. `baseColors[c]` is the colour of the base
 * string hanging in column c; `leaderColors[r]` is the colour of the leader
 * string for row r. Every cell must equal either its column's base colour or
 * its row's leader colour, because those are the only two threads present at
 * that intersection. `validateAlpha` enforces this.
 *
 * The leader alternates direction: row 0 runs left-to-right, row 1 right-to-
 * left, and so on, because the leader physically ends each row on the far side.
 */
export interface AlphaPattern {
  style: 'alpha';
  /** grid[row][col] -> colour index. */
  grid: ColorIndex[][];
  /** One base string per column. */
  baseColors: ColorIndex[];
  /** One leader colour per row (usually all the same). */
  leaderColors: ColorIndex[];
  /** Direction of row 0. Subsequent rows alternate. */
  firstRowDirection: 'ltr' | 'rtl';
}

/* -------------------------------------------------------------------------- */
/* Chain / motif patterns                                                     */
/* -------------------------------------------------------------------------- */

export type MotifKind = 'daisy' | 'cherry' | 'strawberry' | 'heart';

/** A plain run of chain between motifs. `knots` is the count PER SIDE. */
export interface ChainSegment {
  kind: 'chain';
  knots: number;
}

/**
 * One motif. `rounds` is how many times the petal spread-and-return cycle is
 * repeated, which makes the motif taller: 1 is a compact daisy, 2 is a fuller
 * flower. The colours come from the pattern's strand setup, not from here,
 * because the same six strands run the whole length of the bracelet.
 */
export interface MotifSegment {
  kind: 'motif';
  motif: MotifKind;
  rounds: number;
}

export type ChainModule = ChainSegment | MotifSegment;

/**
 * A chain bracelet is not a grid. It is a sequence of modules worked on six
 * strands whose left-to-right order is restored at the end of every module.
 *
 * Setup order, left to right, is:
 *   [chain] [accent] [motif] [motif] [accent] [chain]
 * i.e. strand 0 and 5 do the chain, 1 and 4 are the motif centre, 2 and 3 are
 * the petals/body of the motif.
 */
export interface ChainPattern {
  style: 'chain';
  /** Exactly six entries, in setup order, left to right. */
  strandColors: ColorIndex[];
  modules: ChainModule[];
}

export type PatternBody = NormalPattern | AlphaPattern | ChainPattern;

/* -------------------------------------------------------------------------- */
/* Sizing                                                                     */
/* -------------------------------------------------------------------------- */

export interface Sizing {
  /** Wrist circumference in cm, before ease. */
  wristCm: number;
  /** Extra slack added over the wrist so it is comfortable, in cm. */
  easeCm: number;
  /** How the bracelet fastens, which changes how much tail thread is needed. */
  closure: 'tie' | 'loop-and-tie';
}

/* -------------------------------------------------------------------------- */
/* A complete design                                                          */
/* -------------------------------------------------------------------------- */

export interface Design {
  /** Schema version, so old shared links keep working (or fail loudly). */
  v: 1;
  /** Short human label, e.g. "Sunset Chevron". */
  name: string;
  /** One sentence describing what it looks like. */
  blurb: string;
  palette: PaletteEntry[];
  sizing: Sizing;
  body: PatternBody;
  /** The seed that produced this design, kept so results stay reproducible. */
  seed: string;
}

/* -------------------------------------------------------------------------- */
/* Row geometry helpers (normal patterns)                                     */
/* -------------------------------------------------------------------------- */

/** Number of knots in row `r` (0-based) of a normal pattern. */
export function knotsInRow(strandCount: number, r: number): number {
  return r % 2 === 0 ? strandCount / 2 : strandCount / 2 - 1;
}

/**
 * Position of the LEFT string of the k-th knot in row r.
 * Odd (0-based even) rows start at 0; offset rows start at 1.
 */
export function knotLeftPosition(r: number, k: number): number {
  return r % 2 === 0 ? 2 * k : 2 * k + 1;
}

/** Total knots across a whole normal pattern. */
export function totalNormalKnots(strandCount: number, rowCount: number): number {
  let total = 0;
  for (let r = 0; r < rowCount; r++) total += knotsInRow(strandCount, r);
  return total;
}
